import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useConnectionState, useLocalParticipant, useParticipants } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import {
  reconcileLiveInterviewTranscriptWorker,
  saveLiveTranscriptSegment,
} from "../api/liveInterviewApi";
import {
  getParticipantRoleFromIdentity,
  getRoleSpeakerLabel,
  getSpeechRecognition,
  normalizeTranscriptSpeakerRole,
  parseParticipantMetadata,
  pickRicherTranscript,
  resolveSpeechRecognitionLanguage,
} from "./liveTranscriptUtils";
import { isBrowserTranscriptFallbackEnabled } from "./transcriptFeatureFlags";

const FATAL_SPEECH_ERRORS = new Set([
  "audio-capture",
  "not-allowed",
  "service-not-allowed",
]);

// `no-speech` fires whenever Chrome's SpeechRecognition session ends without
// hearing anyone — normal during silences. `aborted` fires on our own stop.
// `network` is a transient Web-Speech-API blip. All three should restart
// silently and not flood the console as red errors.
const BENIGN_SPEECH_ERRORS = new Set(["no-speech", "aborted", "network"]);
// Browser interim text is provisional. Persist it sparingly; the server
// worker remains the trusted source for adaptive-question progress and finals
// still use the shorter, separate debounce below.
const INTERIM_SAVE_MS = 1500;
const FINAL_SAVE_DEBOUNCE_MS = 1400;
const WORKER_RECONCILE_MS = 2000;

const isTranscriptDisabledError = (err) =>
  Boolean(
    err?.status === 403 &&
      (
        err?.payload?.transcript_enabled === false ||
        /transcript is turned off/i.test(String(err?.message || ""))
      )
  );

const LiveTranscriptCapture = ({
  roomId,
  bookingId = "",
  speakerName,
  speakerRole,
  actorEmail = "",
  actorIdentity = "",
  actorRole = "",
  inviteToken = "",
  readOnly = false,
  transcriptEnabled = true,
  onServerTranscriptDisabled,
  onTranscriptSegmentSaved,
  onStatusChange,
}) => {
  const recognitionRef = useRef(null);
  const intentionalStopRef = useRef(false);
  const sttEpochRef = useRef(0);
  const interimSaveTimerRef = useRef(null);
  const finalSaveTimerRef = useRef(null);
  const pendingInterimTextRef = useRef("");
  const pendingFinalResultsRef = useRef(new Map());
  const lastSavedInterimTextRef = useRef("");
  const stopCaptureRef = useRef(null);
  const workerReconcileTimerRef = useRef(null);
  const participantSignatureRef = useRef("");
  const transcriptEnabledRef = useRef(transcriptEnabled);
  const roomConnectedRef = useRef(false);
  const isLeavingRef = useRef(false);
  const micPublishedLiveRef = useRef(false);
  const serverTranscriptDisabledRef = useRef(false);
  const previousTranscriptEnabledRef = useRef(transcriptEnabled);
  const activeRoomIdRef = useRef(String(roomId || "").trim());
  const componentMountedRef = useRef(true);
  const savedSegmentSequenceRef = useRef(0);
  const transcriptCallbackSessionRef = useRef(0);
  const transcriptCallbackActiveRef = useRef(false);

  const [captureActive, setCaptureActive] = useState(false);
  const [serverTranscriptDisabled, setServerTranscriptDisabled] = useState(false);
  const [speechBlocked, setSpeechBlocked] = useState(false);
  const [lastError, setLastError] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState("");

  const connectionState = useConnectionState();
  const participants = useParticipants();
  const { localParticipant, isMicrophoneEnabled, microphoneTrack } = useLocalParticipant();

  const localParticipantMetadata = parseParticipantMetadata(localParticipant?.metadata);
  const participantIdentity = localParticipant?.identity || actorIdentity || "";
  const participantRole =
    normalizeTranscriptSpeakerRole(
      localParticipantMetadata.role ||
        actorRole ||
        speakerRole ||
        getParticipantRoleFromIdentity(participantIdentity)
    );
  const effectiveSpeakerRole = participantRole || "speaker";
  const resolvedSpeakerName =
    String(
      speakerName ||
      localParticipant?.name ||
      localParticipantMetadata.display_name ||
      participantIdentity ||
      actorIdentity ||
      "",
    ).trim() ||
    getRoleSpeakerLabel(effectiveSpeakerRole) ||
    "Speaker";
  const localAudioTrackMuted =
    microphoneTrack?.isMuted ?? microphoneTrack?.track?.isMuted ?? !isMicrophoneEnabled;
  const localAudioTrackEnabled =
    // LiveKit track refs can be temporarily undefined right after join/unmute.
    // Default to true when mic is enabled so capture can still start and save.
    microphoneTrack?.track?.mediaStreamTrack?.enabled ?? microphoneTrack?.isEnabled ?? isMicrophoneEnabled;
  const roomConnected = connectionState === ConnectionState.Connected;
  const isLeavingOrDisconnected =
    connectionState === ConnectionState.Disconnected ||
    connectionState === ConnectionState.Disconnecting;
  const micPublishedLive =
    Boolean(isMicrophoneEnabled) && !localAudioTrackMuted && localAudioTrackEnabled !== false;
  const browserFallbackEnabled = isBrowserTranscriptFallbackEnabled();
  const recognitionSupported = Boolean(getSpeechRecognition());

  useLayoutEffect(() => {
    transcriptEnabledRef.current = transcriptEnabled;
  }, [transcriptEnabled]);

  useLayoutEffect(() => {
    roomConnectedRef.current = roomConnected;
  }, [roomConnected]);

  useLayoutEffect(() => {
    isLeavingRef.current = isLeavingOrDisconnected;
  }, [isLeavingOrDisconnected]);

  useLayoutEffect(() => {
    micPublishedLiveRef.current = micPublishedLive;
  }, [micPublishedLive]);

  useLayoutEffect(() => {
    serverTranscriptDisabledRef.current = serverTranscriptDisabled;
  }, [serverTranscriptDisabled]);

  useLayoutEffect(() => {
    activeRoomIdRef.current = String(roomId || "").trim();
  }, [roomId]);

  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (previousTranscriptEnabledRef.current !== transcriptEnabled) {
      previousTranscriptEnabledRef.current = transcriptEnabled;
      setServerTranscriptDisabled(false);
      setSpeechBlocked(false);
      setLastError("");
    }
  }, [transcriptEnabled]);

  const clearTranscriptSaveTimers = useCallback(() => {
    if (interimSaveTimerRef.current) {
      window.clearTimeout(interimSaveTimerRef.current);
      interimSaveTimerRef.current = null;
    }
    if (finalSaveTimerRef.current) {
      window.clearTimeout(finalSaveTimerRef.current);
      finalSaveTimerRef.current = null;
    }
  }, []);

  const resetPendingTranscriptBuffers = useCallback(() => {
    pendingInterimTextRef.current = "";
    pendingFinalResultsRef.current.clear();
    lastSavedInterimTextRef.current = "";
  }, []);

  const saveSegment = useCallback(
    async (text, epochAtStart, isFinal = true) => {
      const safeRoomId = String(roomId || "").trim();
      const cleanText = String(text || "").trim();
      if (!safeRoomId || !cleanText) return;
      if (epochAtStart !== sttEpochRef.current) return;
      if (!transcriptEnabledRef.current || !roomConnectedRef.current || isLeavingRef.current) return;
      if (!micPublishedLiveRef.current || serverTranscriptDisabledRef.current) return;

      const saveSequence = savedSegmentSequenceRef.current + 1;
      savedSegmentSequenceRef.current = saveSequence;
      const callbackSessionAtSave = transcriptCallbackSessionRef.current;

      try {
        const payload = await saveLiveTranscriptSegment(safeRoomId, {
          source: "browser",
          actor_email: actorEmail,
          actor_identity: actorIdentity || participantIdentity,
          role: effectiveSpeakerRole,
          invite_token: inviteToken,
          booking_id: bookingId || undefined,
          transcript_text: cleanText,
          speaker_name: resolvedSpeakerName,
          speaker_role: effectiveSpeakerRole,
          speaker_identity: participantIdentity || actorIdentity,
          is_final: Boolean(isFinal),
        });
        setLastSavedAt(new Date().toISOString());
        setLastError("");
        const segment = payload?.segment;
        if (
          segment?.segment_id &&
          componentMountedRef.current &&
          activeRoomIdRef.current === safeRoomId &&
          epochAtStart === sttEpochRef.current &&
          transcriptCallbackActiveRef.current &&
          callbackSessionAtSave === transcriptCallbackSessionRef.current &&
          transcriptEnabledRef.current &&
          roomConnectedRef.current &&
          !isLeavingRef.current &&
          micPublishedLiveRef.current &&
          !serverTranscriptDisabledRef.current
        ) {
          onTranscriptSegmentSaved?.({
            roomId: safeRoomId,
            segment,
            sequence: saveSequence,
          });
        }
      } catch (err) {
        if (isTranscriptDisabledError(err)) {
          setServerTranscriptDisabled(true);
          setLastError("Server transcript is turned off for this room.");
          onServerTranscriptDisabled?.();
          stopCaptureRef.current?.("server_transcript_disabled");
          return;
        }
        setLastError(err?.message || "Transcript segment could not be saved.");
        throw err;
      }
    },
    [
      actorEmail,
      actorIdentity,
      bookingId,
      effectiveSpeakerRole,
      inviteToken,
      onServerTranscriptDisabled,
      onTranscriptSegmentSaved,
      participantIdentity,
      roomId,
      resolvedSpeakerName,
    ]
  );

  const flushPendingFinalTranscript = useCallback(
    async (epochAtStart, includeInterimFallback = true) => {
      clearTranscriptSaveTimers();
      const finalResults = Array.from(pendingFinalResultsRef.current.entries())
        .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
        .map(([, text]) => String(text || "").trim())
        .filter(Boolean);
      const interimFallbackText = pendingInterimTextRef.current.trim();
      resetPendingTranscriptBuffers();
      const textsToSave = finalResults.length
        ? finalResults
        : includeInterimFallback
          ? [interimFallbackText].filter(Boolean)
          : [];
      for (const text of textsToSave) {
        try {
          await saveSegment(text, epochAtStart, true);
        } catch (err) {
          console.error("[LIVEKIT_TRANSCRIPT] auto-capture final flush failed", err);
        }
      }
    },
    [clearTranscriptSaveTimers, resetPendingTranscriptBuffers, saveSegment]
  );

  const stopCapture = useCallback((reason = "manual_stop") => {
    const recognition = recognitionRef.current;
    transcriptCallbackActiveRef.current = false;
    transcriptCallbackSessionRef.current += 1;
    intentionalStopRef.current = true;
    recognitionRef.current = null;
    sttEpochRef.current += 1;
    clearTranscriptSaveTimers();
    resetPendingTranscriptBuffers();
    setCaptureActive(false);
    if (recognition) {
      console.info("[LIVEKIT_TRANSCRIPT] auto-capture stop", {
        reason,
        participantIdentity,
        role: effectiveSpeakerRole,
      });
      try {
        recognition.stop();
      } catch {
        intentionalStopRef.current = false;
      }
    }
  }, [
    clearTranscriptSaveTimers,
    effectiveSpeakerRole,
    participantIdentity,
    resetPendingTranscriptBuffers,
  ]);

  const scheduleWorkerReconcile = useCallback(
    (reason = "participants_changed") => {
      if (readOnly || !transcriptEnabledRef.current || !roomConnectedRef.current) {
        return;
      }
      const safeRoomId = String(roomId || "").trim();
      if (!safeRoomId) return;
      if (workerReconcileTimerRef.current) {
        window.clearTimeout(workerReconcileTimerRef.current);
      }
      workerReconcileTimerRef.current = window.setTimeout(async () => {
        workerReconcileTimerRef.current = null;
        try {
          await reconcileLiveInterviewTranscriptWorker(safeRoomId, {
            actor_email: actorEmail,
            actor_identity: actorIdentity || participantIdentity,
            role: effectiveSpeakerRole,
            invite_token: inviteToken,
            reason,
          });
        } catch (err) {
          console.warn("[LIVEKIT_TRANSCRIPT] server worker reconcile failed", err);
        }
      }, WORKER_RECONCILE_MS);
    },
    [
      actorEmail,
      actorIdentity,
      effectiveSpeakerRole,
      inviteToken,
      participantIdentity,
      readOnly,
      roomId,
    ]
  );

  useEffect(() => {
    stopCaptureRef.current = stopCapture;
  }, [stopCapture]);

  useEffect(() => {
    if (!roomConnected || !transcriptEnabled || readOnly || !micPublishedLive) {
      return undefined;
    }
    scheduleWorkerReconcile("microphone_live");
    return undefined;
  }, [
    micPublishedLive,
    readOnly,
    roomConnected,
    scheduleWorkerReconcile,
    transcriptEnabled,
  ]);

  useEffect(() => {
    if (!roomConnected || !transcriptEnabled || readOnly) {
      participantSignatureRef.current = "";
      return undefined;
    }
    scheduleWorkerReconcile("room_connected");
    const signature = participants
      .map((participant) => participant.identity || "")
      .filter(Boolean)
      .sort()
      .join("|");
    if (signature && signature !== participantSignatureRef.current) {
      participantSignatureRef.current = signature;
      scheduleWorkerReconcile("participants_changed");
    }
    return () => {
      if (workerReconcileTimerRef.current) {
        window.clearTimeout(workerReconcileTimerRef.current);
        workerReconcileTimerRef.current = null;
      }
    };
  }, [
    participants,
    readOnly,
    roomConnected,
    scheduleWorkerReconcile,
    transcriptEnabled,
  ]);

  const startCapture = useCallback(() => {
    const Recognition = getSpeechRecognition();
    if (
      !browserFallbackEnabled ||
      !Recognition ||
      readOnly ||
      speechBlocked ||
      serverTranscriptDisabledRef.current
    ) {
      return;
    }
    if (!transcriptEnabledRef.current || !roomConnectedRef.current || !micPublishedLiveRef.current) {
      return;
    }
    if (recognitionRef.current) {
      return;
    }

    const epochAtStart = sttEpochRef.current;
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = resolveSpeechRecognitionLanguage();
    intentionalStopRef.current = false;
    resetPendingTranscriptBuffers();

    const shouldIgnoreEvent = () =>
      !transcriptEnabledRef.current ||
      !roomConnectedRef.current ||
      isLeavingRef.current ||
      !micPublishedLiveRef.current ||
      serverTranscriptDisabledRef.current ||
      epochAtStart !== sttEpochRef.current;

    const finalizeNaturalRecognitionSession = () => {
      if (recognitionRef.current !== recognition) return false;

      // Start the final-only flush before invalidating this recognition
      // session. The flush snapshots and clears its buffers synchronously,
      // preventing the next session or the debounce timer from sending it
      // again.
      void flushPendingFinalTranscript(epochAtStart, false);
      recognitionRef.current = null;
      transcriptCallbackActiveRef.current = false;
      transcriptCallbackSessionRef.current += 1;
      setCaptureActive(false);
      return true;
    };

    recognition.onresult = (event) => {
      if (shouldIgnoreEvent()) return;
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        if (shouldIgnoreEvent()) return;
        const transcript = String(event.results[index][0]?.transcript || "").trim();
        if (!transcript) continue;

        const isFinal = Boolean(event.results[index].isFinal);
        pendingInterimTextRef.current = pickRicherTranscript(
          pendingInterimTextRef.current,
          transcript,
        );

        if (!isFinal) {
          if (interimSaveTimerRef.current) {
            window.clearTimeout(interimSaveTimerRef.current);
          }
          interimSaveTimerRef.current = window.setTimeout(async () => {
            interimSaveTimerRef.current = null;
            if (shouldIgnoreEvent()) return;
            const interimText = pendingInterimTextRef.current.trim();
            if (!interimText || interimText === lastSavedInterimTextRef.current) return;
            lastSavedInterimTextRef.current = interimText;
            try {
              await saveSegment(interimText, epochAtStart, false);
            } catch (err) {
              console.error("[LIVEKIT_TRANSCRIPT] auto-capture interim save failed", err);
            }
          }, INTERIM_SAVE_MS);
          continue;
        }

        // A final result supersedes its pending browser partial. Do not queue
        // a stale provisional write after the final is already scheduled.
        if (interimSaveTimerRef.current) {
          window.clearTimeout(interimSaveTimerRef.current);
          interimSaveTimerRef.current = null;
        }

        // SpeechRecognition final results are immutable by result index. Keep
        // each index so independent finals are not collapsed into one string;
        // a same-index correction replaces only its pending entry.
        pendingFinalResultsRef.current.set(index, transcript);
        if (finalSaveTimerRef.current) {
          window.clearTimeout(finalSaveTimerRef.current);
        }
        finalSaveTimerRef.current = window.setTimeout(async () => {
          finalSaveTimerRef.current = null;
          if (shouldIgnoreEvent()) return;
          try {
            await flushPendingFinalTranscript(epochAtStart);
          } catch (err) {
            console.error("[LIVEKIT_TRANSCRIPT] auto-capture save failed", err);
          }
        }, FINAL_SAVE_DEBOUNCE_MS);
      }
    };

    recognition.onerror = (event) => {
      const wasIntentionalStop = intentionalStopRef.current;
      intentionalStopRef.current = false;
      const errorCode = String(event?.error || "").trim().toLowerCase();
      const isBenignError = BENIGN_SPEECH_ERRORS.has(errorCode);

      // Intentional stops + benign end-of-silence signals (`no-speech`,
      // `aborted`, `network`) are part of normal SpeechRecognition lifecycle —
      // the useEffect driving shouldCapture restarts on the next tick. Don't
      // spam the console or set lastError for these.
      if (wasIntentionalStop && errorCode === "aborted") {
        setLastError("");
        return;
      }

      if (isBenignError) {
        if (finalizeNaturalRecognitionSession()) {
          setLastError("");
        }
        return;
      }

      if (recognitionRef.current !== recognition) return;
      recognitionRef.current = null;
      transcriptCallbackActiveRef.current = false;
      transcriptCallbackSessionRef.current += 1;
      setCaptureActive(false);

      if (FATAL_SPEECH_ERRORS.has(errorCode)) {
        setSpeechBlocked(true);
      }
      setLastError(
        event?.error
          ? `Browser speech recognition error: ${event.error}`
          : "Browser speech recognition failed."
      );
      console.error("[LIVEKIT_TRANSCRIPT] auto-capture recognition error", {
        participantIdentity,
        role: effectiveSpeakerRole,
        error: errorCode || "unknown",
      });
    };

    recognition.onend = () => {
      const wasIntentionalStop = intentionalStopRef.current;
      intentionalStopRef.current = false;
      if (wasIntentionalStop) return;
      finalizeNaturalRecognitionSession();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      transcriptCallbackActiveRef.current = true;
      setCaptureActive(true);
      setLastError("");
      console.info("[LIVEKIT_TRANSCRIPT] auto-capture start", {
        participantIdentity,
        role: effectiveSpeakerRole,
      });
    } catch (err) {
      recognitionRef.current = null;
      transcriptCallbackActiveRef.current = false;
      transcriptCallbackSessionRef.current += 1;
      intentionalStopRef.current = false;
      setCaptureActive(false);
      setLastError(err?.message || "Browser speech recognition could not start.");
      console.error("[LIVEKIT_TRANSCRIPT] auto-capture start failed", err);
    }
  }, [
    browserFallbackEnabled,
    effectiveSpeakerRole,
    flushPendingFinalTranscript,
    participantIdentity,
    readOnly,
    resetPendingTranscriptBuffers,
    saveSegment,
    speechBlocked,
  ]);

  const shouldCapture =
    !readOnly &&
    browserFallbackEnabled &&
    recognitionSupported &&
    transcriptEnabled &&
    !serverTranscriptDisabled &&
    !speechBlocked &&
    roomConnected &&
    !isLeavingOrDisconnected &&
    micPublishedLive;

  const captureReason = !browserFallbackEnabled
    ? "browser_fallback_disabled"
    : readOnly
      ? "read_only"
      : !recognitionSupported
        ? "speech_recognition_unsupported"
        : !transcriptEnabled
          ? "transcript_disabled"
          : serverTranscriptDisabled
            ? "server_transcript_disabled"
            : speechBlocked
              ? "speech_blocked"
              : !roomConnected
                ? "room_not_connected"
                : isLeavingOrDisconnected
                  ? "room_leaving"
                  : !micPublishedLive
                    ? "microphone_not_live"
                    : captureActive
                      ? "capturing"
                      : "ready";

  useEffect(() => {
    if (!shouldCapture) {
      stopCapture("capture_conditions_changed");
      return;
    }
    if (!recognitionRef.current && !captureActive) {
      startCapture();
    }
  }, [captureActive, shouldCapture, startCapture, stopCapture]);

  useEffect(() => () => stopCapture("room_or_component_disposed"), [roomId, stopCapture]);

  useEffect(() => {
    onStatusChange?.({
      mode: "browser_fallback",
      browserFallbackEnabled,
      recognitionSupported,
      captureActive,
      roomConnected,
      micPublishedLive,
      transcriptEnabled,
      readOnly,
      speechBlocked,
      serverTranscriptDisabled,
      reason: captureReason,
      lastError,
      lastSavedAt,
    });
  }, [
    browserFallbackEnabled,
    captureActive,
    captureReason,
    lastError,
    lastSavedAt,
    micPublishedLive,
    onStatusChange,
    readOnly,
    recognitionSupported,
    roomConnected,
    serverTranscriptDisabled,
    speechBlocked,
    transcriptEnabled,
  ]);

  return null;
};

export default LiveTranscriptCapture;
